/**
 * Whether the embedding pipeline turned this file into retrievable knowledge.
 *
 * Derived server-side (create_agent/app.py `_annotate_training_file_status`) rather than
 * here, because the backend already holds training_files and embedding_index side by
 * side and the rule belongs in one place. "unknown" is for files attached before the
 * backend recorded this — the UI shows no badge rather than guessing.
 */
export type KnowledgeFileStatus =
  | "indexed"
  | "processing"
  | "failed"
  | "unknown";

export interface NormalizedFile {
  bucket: string;
  key: string;
  filename: string;
  /** "default" = bundled guide, "web_crawl" = Firecrawl page, "user" = user-uploaded,
   *  "assistant" = written by the onboarding assistant (icp/services/faq/pricing.md) */
  type?: "default" | "web_crawl" | "user" | "assistant";
  status?: KnowledgeFileStatus;
  /** Plain-language reason a file failed, safe to show the user. */
  statusDetail?: string;
}

/**
 * Normalizes file objects from different backend formats to a consistent structure
 */
export const normalizeFiles = (
  files: any[]
): NormalizedFile[] => {
  if (!Array.isArray(files)) {
    return [];
  }

  return files
    .map((f: any) => {
      if (!f) return null;

      // Be tolerant: some backends may omit bucket and/or filename
      const bucket = f.bucket || f.s3_bucket || f.Bucket || "";
      const key =
        f.key ||
        f.s3_key ||
        f.Key ||
        f.s3Key ||
        f.object_key ||
        f.objectKey;
      const filename =
        f.filename ||
        f.file_name ||
        f.name ||
        f.original_filename ||
        f.originalFilename ||
        (key ? key.split("/").pop() : "Unknown");

      if (!key) return null;

      // Starter files saved before the backend tagged them have no type; their key
      // is the only signal. ("/training/" is shared with user uploads, so crawl
      // pages cannot be told apart by key and get no fallback.)
      const type = (f.type ||
        (key.includes("/starter-knowledge/") ? "assistant" : undefined)) as
        | NormalizedFile["type"]
        | undefined;
      const status = f.status as KnowledgeFileStatus | undefined;
      const statusDetail = f.status_detail || f.statusDetail;
      return {
        bucket,
        key,
        filename,
        ...(type ? { type } : {}),
        ...(status ? { status } : {}),
        ...(statusDetail ? { statusDetail } : {}),
      };
    })
    .filter((f): f is NormalizedFile => f !== null);
};
